import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { PlayIcon, PauseIcon } from 'lucide-react';

import { formatTime } from '@/lib/utils/date';
import { activitiesApi, activityKeys } from '../api/activities.api';
import { ActivityDialog } from './ActivityDialog';

// Main ActivityTimer component for navbar
export function ActivityTimer() {
  const [runTime, setRunTime] = React.useState(0);

  // Fetch active activity
  const { data: activeActivity } = useQuery({
    queryKey: activityKeys.active(),
    queryFn: async () => {
      try {
        return await activitiesApi.getActive();
      } catch {
        return null;
      }
    },
    retry: 1,
    refetchInterval: 30000, // Refetch every 30 seconds to stay in sync
  });

  // Calculate and update running time
  React.useEffect(() => {
    if (!activeActivity?.lastStartTime) {
      setRunTime(0);
      return;
    }

    // If activity has ended, don't run timer
    if (activeActivity.lastEndTime) {
      setRunTime(activeActivity.userEnteredTimeInSeconds || 0);
      return;
    }

    // Base time already tracked before this session
    const base = Number(activeActivity.runTimeInSeconds || 0);

    // Convert server times - use currentTime if available for accurate calculation
    const serverStart = new Date(activeActivity.lastStartTime).getTime();
    const serverNow = activeActivity.currentTime
      ? new Date(activeActivity.currentTime).getTime()
      : Date.now();

    // Elapsed time already on the server
    const elapsedOnServer = Math.max(0, Math.floor((serverNow - serverStart) / 1000));

    // Combine base + elapsed
    const initialRun = base + elapsedOnServer;

    // Track when this data was fetched (on client)
    const fetchPerf = performance.now();

    // Set the starting display
    setRunTime(initialRun);

    // Update smoothly using performance.now (monotonic, immune to system clock changes)
    const interval = setInterval(() => {
      const elapsedSinceFetch = Math.floor((performance.now() - fetchPerf) / 1000);
      setRunTime(() => initialRun + elapsedSinceFetch);
    }, 1000);

    return () => clearInterval(interval);
  }, [activeActivity]);

  return (
    <div className="flex flex-col items-start gap-2">
      <div className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all p-2 py-1 border bg-background shadow-xs">
        <span className="font-mono">{formatTime(runTime)}</span>
        {activeActivity ? (
          <ActivityDialog
            activityId={activeActivity.id}
            trigger={
              <button className="hover:bg-muted rounded p-0.5">
                <PauseIcon className="w-4 h-4" />
              </button>
            }
          />
        ) : (
          <ActivityDialog
            trigger={
              <button className="hover:bg-muted rounded p-0.5">
                <PlayIcon className="w-4 h-4" />
              </button>
            }
          />
        )}
      </div>
    </div>
  );
}
